import React from "react";

type Props = { children: React.ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  reset = (): void => this.setState({ error: null });

  override render(): React.ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900 p-6 text-ink-50">
        <div className="max-w-xl rounded-lg border border-ink-600 bg-ink-800 p-6 shadow-lg">
          <h1 className="mb-2 text-lg font-semibold text-ink-50">
            Something went wrong
          </h1>
          <p className="mb-3 text-sm text-ink-300">
            The page hit a runtime error. Your data is safe — this is a UI
            crash, not a save failure. Reload to recover.
          </p>
          <pre className="mb-4 max-h-48 overflow-auto rounded border border-ink-700 bg-ink-900 p-3 font-mono text-[12px] text-ink-200">
            {this.state.error.message}
          </pre>
          <div className="flex gap-2">
            <button
              onClick={() => window.location.reload()}
              className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-sm font-medium text-ink-50 hover:bg-slack-aubergineLight"
            >
              Reload page
            </button>
            <button
              onClick={this.reset}
              className="rounded-md border border-ink-500 bg-ink-800 px-3 py-1.5 text-sm text-ink-50 hover:border-ink-400"
            >
              Dismiss and continue
            </button>
          </div>
        </div>
      </div>
    );
  }
}
